import { useEffect, useState } from "react";
import { Check, FlaskConical, Loader2, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function PackageCard({ p, onBuy, onSimulate, busy, i }) {
  const sub = !!p.interval;
  return (
    <div className={`surface p-6 flex flex-col fade-up relative ${sub ? "!border-[#E50914]" : ""}`} style={{ animationDelay: `${i * 70}ms` }}
      data-testid={`package-${p.lookup_key}`}>
      {sub && <span className="absolute -top-3 left-6 bg-[#E50914] text-white mono text-[10px] tracking-widest px-2 py-1">ILLIMITATO</span>}
      <div className="eyebrow mb-2">{sub ? "Abbonamento" : "Ricarica"}</div>
      <h3 className="font-display text-2xl font-bold uppercase">{p.name}</h3>
      <div className="mt-4 flex items-end gap-1">
        <span className="mono text-4xl font-bold text-white" data-testid={`package-price-${p.lookup_key}`}>€{p.amount.toFixed(0)}</span>
        <span className="text-slate-500 text-sm mb-1">{sub ? "/ mese" : ""}</span>
      </div>
      <p className="text-sm text-slate-400 mt-3 flex items-start gap-2 flex-1"><Check size={16} className="text-[#E50914] shrink-0 mt-0.5" /> {p.description}</p>
      <button onClick={() => onBuy(p.lookup_key)} disabled={!!busy} className={`${sub ? "btn-red" : "btn-ghost"} mt-6 py-3 flex items-center justify-center gap-2`}
        data-testid={`buy-${p.lookup_key}-button`}>
        {busy === p.lookup_key && <Loader2 size={16} className="animate-spin" />} {sub ? "Attiva abbonamento" : "Acquista"}
      </button>
      {p.simulation && (
        <button onClick={() => onSimulate(p.lookup_key)} disabled={!!busy} className="mt-2 py-2.5 text-sm border border-dashed border-amber-500/50 text-amber-300 rounded hover:bg-amber-500/10 transition-colors flex items-center justify-center gap-2"
          data-testid={`simulate-${p.lookup_key}-button`}>
          {busy === `sim_${p.lookup_key}` ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Simula acquisto (test)
        </button>
      )}
    </div>
  );
}

export default function Credits() {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => setPackages(data));
    window.addEventListener("focus", refreshUser);
    return () => window.removeEventListener("focus", refreshUser);
  }, [refreshUser]);
  const buy = async (lookup_key) => {
    // Stripe Checkout cannot load inside the embedded preview: open it in a new tab
    const win = window.open("", "_blank");
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      if (win) {
        win.location.href = data.checkout_url;
        toast.info("Pagamento aperto in una nuova scheda. Al termine torna qui: il saldo si aggiorna da solo.");
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      if (win) win.close();
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };
  const simulate = async (lookup_key) => {
    setBusy(`sim_${lookup_key}`);
    try {
      await api.post("/payments/simulate", { lookup_key });
      await refreshUser();
      toast.success("Acquisto simulato completato: saldo aggiornato");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div>
      <div className="eyebrow mb-2">Ricarica Crediti / Acquista Supporto</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase">Crediti e abbonamento</h1>
      <p className="text-slate-400 mt-3 max-w-2xl">1 credito = 1 ticket con appuntamento. Con l'abbonamento mensile apri ticket illimitati senza consumare crediti.</p>
      <div className="flex flex-wrap gap-4 mt-6">
        <div className="surface-2 px-4 py-3 text-sm" data-testid="current-balance">Saldo: <span className="mono font-bold text-white">{user.credits}</span> crediti
          {user.subscription_active && <span className="text-green-400 ml-3">· Abbonamento attivo fino al {new Date(user.subscription_until).toLocaleDateString("it-IT")}</span>}
          <button onClick={refreshUser} className="ml-3 text-slate-400 hover:text-white inline-flex items-center gap-1 text-xs" data-testid="refresh-balance-button"><RefreshCw size={12} /> Aggiorna</button>
        </div>
        <div className="surface-2 px-4 py-3 text-sm flex items-center gap-2 text-amber-300" data-testid="test-mode-banner">
          <FlaskConical size={16} /> Modalità test attiva: usa la carta <span className="mono text-white">4242 4242 4242 4242</span>, scadenza futura, CVC qualsiasi.
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        {packages.map((p, i) => <PackageCard key={p.lookup_key} p={p} i={i} onBuy={buy} onSimulate={simulate} busy={busy} />)}
      </div>
    </div>
  );
}
